import React from 'react';
import {useTranslation} from 'react-i18next';
import {UseQueryResult} from '@tanstack/react-query';
import Button from '../../../incase-domain/components/button/Button';
import TransitionWrapper from '../../components/pageTransitions/PageTransitionWrapper';
import ConnectionsTableActive from './ConnectionsTableActive';
import {ConnectionRequestType, IConnection, IConnectionRequest} from '../../../incase-domain/connection';
import {IPaginatedResult} from '../../../incase-domain/search';
import PageHeader from '../../components/common/header/PageHeader';
import ConnectionsTable from './ConnectionsTable';
import {useConnectionsPaginatedQuery, useConnectionRequestsPaginatedQuery} from '../../../hooks/connectionQueries';
import {ConnectionsTab, useConnectionsListSearchParams} from './connectionsListSearchParams';

function ConnectionsPage(): JSX.Element {
    const {t} = useTranslation();

    const {activeTab, setActiveTab, listParams, setListParams} = useConnectionsListSearchParams();

    const paginatedSearch = {
        pageNumber: listParams.page,
        pageSize: listParams.size,
        searchString: listParams.search,
    };

    const connections: UseQueryResult<IPaginatedResult<IConnection>> = useConnectionsPaginatedQuery(paginatedSearch);

    const receivedConnectionRequests: UseQueryResult<IPaginatedResult<IConnectionRequest>> = useConnectionRequestsPaginatedQuery(
        ConnectionRequestType.RECEIVED,
        paginatedSearch
    );

    const sentConnectionRequests: UseQueryResult<IPaginatedResult<IConnectionRequest>> = useConnectionRequestsPaginatedQuery(
        ConnectionRequestType.SENT,
        paginatedSearch
    );

    return (
        <div className="l-container">
            <PageHeader title={t('connections.title')} />

            <div className="u-flex u-gap-1 u-mb-3">
                <Button
                    variant={activeTab === ConnectionsTab.Active ? 'solid' : 'soft'}
                    size="sm"
                    leading="solar:heart-linear"
                    onClick={() => setActiveTab(ConnectionsTab.Active)}
                >
                    {t('connections.active')}
                </Button>
                <Button
                    variant={activeTab === ConnectionsTab.Requests ? 'solid' : 'soft'}
                    size="sm"
                    leading="solar:user-hand-up-linear"
                    onClick={() => setActiveTab(ConnectionsTab.Requests)}
                >
                    {t('connections.requests')}
                </Button>
                <Button
                    variant={activeTab === ConnectionsTab.Sent ? 'solid' : 'soft'}
                    size="sm"
                    leading="solar:plain-3-outline"
                    onClick={() => setActiveTab(ConnectionsTab.Sent)}
                >
                    {t('connections.sent')}
                </Button>
            </div>

            <TransitionWrapper transitionKey={activeTab}>
                <div>
                    {activeTab === ConnectionsTab.Active && (
                        <ConnectionsTableActive
                            connections={connections}
                            listParams={listParams}
                            setListParams={setListParams}
                            onNewConnectionRequestAdded={() => sentConnectionRequests.refetch()}
                        />
                    )}
                    {activeTab === ConnectionsTab.Requests && (
                        <ConnectionsTable
                            mode="received"
                            data={receivedConnectionRequests}
                            listParams={listParams}
                            setListParams={setListParams}
                        />
                    )}

                    {activeTab === ConnectionsTab.Sent && (
                        <ConnectionsTable mode="sent" data={sentConnectionRequests} listParams={listParams} setListParams={setListParams} />
                    )}
                </div>
            </TransitionWrapper>
        </div>
    );
}

export default ConnectionsPage;
